import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  Button,
  LinearProgress,
  Paper,
  TextField,
  Typography
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import BusinessIcon from '@material-ui/icons/Business';
import RichEditor from '../../../components/RichEditor/RichEditor';

const useStyles = makeStyles((theme) => ({
  container: {
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.background.default
  },
  header: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  contentContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2)
    }
  },
  contentInner: {
    maxWidth: 800,
    margin: '0 auto'
  },
  stepContent: {
    animation: '$fadeIn 0.3s ease-in'
  },
  '@keyframes fadeIn': {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0)' }
  },
  footer: {
    padding: theme.spacing(2, 3),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(2)
  },
  footerActions: {
    display: 'flex',
    gap: theme.spacing(2)
  },
  field: {
    marginBottom: theme.spacing(3)
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(3),
    color: theme.palette.text.primary
  },
  sectionDescription: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
    lineHeight: 1.6
  },
  progressText: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    fontWeight: 500
  },
  completedIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(0.5)
  },
  photoUploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(4)
  },
  avatar: {
    width: 200,
    height: 200,
    fontSize: '4rem',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.main,
    border: `4px solid ${theme.palette.divider}`
  },
  uploadButton: {
    minWidth: 200
  },
  richEditorContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    minHeight: 300,
    '& .RichEditor-root': {
      minHeight: 250
    }
  }
}));

const CompanyOnboardingStepper = ({
  onSave,
  initialData = {},
  loading = false,
  logoUrl,
  onLogoUpload
}) => {
  const classes = useStyles();

  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    location: '',
    siteURL: '',
    email: '',
    detail: null, // Rich text editor expects null or Draft.js content object
    invoiceEmail: '',
    invoiceAddress: ''
  });
  const [uploadedLogo, setUploadedLogo] = useState(null);

  const steps = [
    { label: 'Company Logo', field: 'logo', optional: false },
    { label: 'Company Name', field: 'name', optional: false },
    { label: 'Company Location', field: 'location', optional: false },
    { label: 'Company Website URL', field: 'siteURL', optional: false },
    { label: 'Company Email', field: 'email', optional: false },
    { label: 'About Company', field: 'detail', optional: true },
    { label: 'Invoice Info', field: 'invoice', optional: true }
  ];

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      setFormData((prev) => ({
        ...prev,
        ...initialData,
        name: initialData.name || prev.name,
        location: initialData.location || prev.location,
        siteURL: initialData.siteURL || prev.siteURL,
        email: initialData.email || prev.email,
        detail: initialData.detail || prev.detail,
        invoiceEmail: initialData.invoiceEmail || prev.invoiceEmail,
        invoiceAddress: initialData.invoiceAddress || prev.invoiceAddress
      }));
    }
  }, [initialData]);

  const handleChange = (field) => (event) => {
    const newValue = event.target.value;
    const config = getFieldConfig(field);

    // Apply maxLength validation if specified
    if (config.maxLength && newValue.length > config.maxLength) {
      return;
    }

    setFormData({
      ...formData,
      [field]: newValue
    });
  };

  const handleNext = () => {
    if (activeStep < steps.length - 1) {
      setActiveStep((prevStep) => prevStep + 1);
    } else {
      // Last step - save and complete
      const dataToSave = {
        ...formData,
        logoUrl: uploadedLogo?.publicUrl || logoUrl || formData.logoUrl
      };
      console.log('💾 [CompanyOnboardingStepper] Saving data:', dataToSave);
      console.log(
        '💾 [CompanyOnboardingStepper] detail field:',
        dataToSave.detail
      );
      onSave(dataToSave, true);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedLogo({ file });
      onLogoUpload(file);
    }
  };

  const isStepValid = () => {
    const currentStep = steps[activeStep];
    const field = currentStep.field;

    if (field === 'logo') {
      return uploadedLogo || logoUrl;
    }

    if (currentStep.optional) {
      return true;
    }

    if (field === 'invoice') {
      return true; // Invoice fields are optional
    }

    return formData[field] && formData[field].trim() !== '';
  };

  const handleKeyPress = (event) => {
    const currentStep = steps[activeStep];
    const field = currentStep.field;
    const config = getFieldConfig(field);

    if (event.key === 'Enter') {
      if (config.multiline) {
        if (event.ctrlKey || event.metaKey) {
          event.preventDefault();
          if (isStepValid()) {
            handleNext();
          }
        }
      } else {
        event.preventDefault();
        if (isStepValid()) {
          handleNext();
        }
      }
    }
  };

  const getFieldConfig = (field) => {
    switch (field) {
      case 'logo':
        return {
          label: 'Company Logo',
          title: 'Upload your company logo',
          description:
            'Add your company logo to build trust with candidates. Use a square image for best results.'
        };
      case 'name':
        return {
          label: 'Company Name',
          placeholder: 'e.g., Acme Corporation',
          helperText:
            "Your company's brand/trade name: without Inc., Ltd., B.V., Pte., etc.",
          multiline: false,
          rows: 1,
          title: 'What is your company name?',
          description: 'Enter your company or organization name.'
        };
      case 'location':
        return {
          label: 'Company Location',
          placeholder: 'e.g., San Francisco, CA or London, UK',
          helperText: 'City and state/country where your company is based',
          multiline: false,
          rows: 1,
          title: 'Where is your company located?',
          description: 'Help candidates know where your company operates from.'
        };
      case 'siteURL':
        return {
          label: 'Company Website URL',
          placeholder: 'https://www.yourcompany.com',
          helperText: 'Your company website URL',
          multiline: false,
          rows: 1,
          title: 'What is your company website?',
          description:
            'Share your company website so candidates can learn more about you.'
        };
      case 'email':
        return {
          label: 'Company Email',
          placeholder: 'contact@yourcompany.com',
          helperText:
            'This email stays private and is used for invoices and edit links',
          multiline: false,
          rows: 1,
          type: 'email',
          title: 'What is your company email?',
          description:
            'We use this to send you invoices and important updates. Keep it accessible!'
        };
      case 'detail':
        return {
          label: 'About Company',
          placeholder:
            'Tell candidates about your company, mission, culture, and what makes you unique...',
          title: 'Tell us about your company',
          description:
            'Share your company story, mission, culture, and what makes you a great place to work. Use the formatting tools to make it stand out.',
          isRichText: true
        };
      case 'invoice':
        return {
          label: 'Invoice Information',
          title: 'Invoice information (Optional)',
          description:
            'Add billing details for your invoices. You can skip this and add it later.'
        };
      default:
        return {
          label: field,
          placeholder: '',
          helperText: '',
          multiline: false,
          rows: 1,
          title: 'Enter your information',
          description: ''
        };
    }
  };

  const getStepContent = (stepIndex) => {
    const step = steps[stepIndex];
    const field = step.field;
    const config = getFieldConfig(field);

    if (field === 'logo') {
      return (
        <Box className={classes.photoUploadContainer}>
          <Avatar className={classes.avatar} src={logoUrl}>
            {!logoUrl && <BusinessIcon style={{ fontSize: '5rem' }} />}
          </Avatar>
          <input
            accept="image/*"
            style={{ display: 'none' }}
            id="logo-upload-input"
            type="file"
            onChange={handleLogoUpload}
          />
          <label htmlFor="logo-upload-input">
            <Button
              variant="contained"
              color="primary"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={loading}
              className={classes.uploadButton}>
              {logoUrl ? 'Change Logo' : 'Upload Logo'}
            </Button>
          </label>
          {loading && (
            <Typography variant="body2" color="textSecondary">
              Uploading logo...
            </Typography>
          )}
        </Box>
      );
    }

    if (field === 'detail') {
      return (
        <Box className={classes.stepContent}>
          <Typography variant="h4" className={classes.sectionTitle}>
            {config.title}
          </Typography>
          {config.description && (
            <Typography variant="body1" className={classes.sectionDescription}>
              {config.description}
            </Typography>
          )}
          <Paper className={classes.richEditorContainer}>
            <RichEditor
              placeholder={config.placeholder}
              value={formData.detail}
              onChange={(content) => {
                console.log('📝 [RichEditor] Content changed:', content);
                setFormData({
                  ...formData,
                  detail: content
                });
              }}
            />
          </Paper>
        </Box>
      );
    }

    if (field === 'invoice') {
      return (
        <Box className={classes.stepContent}>
          <Typography variant="h4" className={classes.sectionTitle}>
            {config.title}
          </Typography>
          {config.description && (
            <Typography variant="body1" className={classes.sectionDescription}>
              {config.description}
            </Typography>
          )}
          <TextField
            fullWidth
            label="Invoice Email (Optional)"
            placeholder="accounting@yourcompany.com"
            variant="outlined"
            value={formData.invoiceEmail || ''}
            onChange={handleChange('invoiceEmail')}
            helperText="We'll send a copy of invoices to this email"
            className={classes.field}
            type="email"
          />
          <TextField
            fullWidth
            label="Invoice Address (Optional)"
            placeholder="123 Main St, Suite 100, City, Country, ZIP/Postal Code"
            variant="outlined"
            value={formData.invoiceAddress || ''}
            onChange={handleChange('invoiceAddress')}
            helperText="Your company's full invoice address including VAT number if applicable"
            multiline
            minRows={4}
            className={classes.field}
          />
        </Box>
      );
    }

    return (
      <Box className={classes.stepContent}>
        <Typography variant="h4" className={classes.sectionTitle}>
          {config.title}
        </Typography>
        {config.description && (
          <Typography variant="body1" className={classes.sectionDescription}>
            {config.description}
          </Typography>
        )}
        <TextField
          fullWidth
          label={config.label}
          placeholder={config.placeholder}
          variant="outlined"
          value={formData[field] || ''}
          onChange={handleChange(field)}
          onKeyPress={handleKeyPress}
          helperText={config.helperText}
          multiline={config.multiline}
          minRows={config.multiline ? config.rows : undefined}
          error={
            config.maxLength &&
            (formData[field]?.length || 0) >= config.maxLength
          }
          type={config.type || 'text'}
          autoFocus
          className={classes.field}
        />
      </Box>
    );
  };

  const progress = ((activeStep + 1) / steps.length) * 100;
  const isLastStep = activeStep === steps.length - 1;

  return (
    <Box>
      <Box className={classes.container}>
        {/* Header */}
        <Box className={classes.header}>
          <Box>
            <Typography variant="h5" style={{ fontWeight: 600 }}>
              Complete Your Company Profile
            </Typography>
            <Typography variant="body2" className={classes.progressText}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
          </Box>
        </Box>

        {/* Progress Bar */}
        <LinearProgress variant="determinate" value={progress} />

        {/* Content */}
        <Box className={classes.contentContainer}>
          <Box className={classes.contentInner}>
            {getStepContent(activeStep)}
          </Box>
        </Box>

        {/* Footer */}
        <Box className={classes.footer}>
          <Typography variant="body2" className={classes.progressText}>
            {Math.round(progress)}% Complete
          </Typography>
          <Box className={classes.footerActions}>
            {activeStep > 0 && (
              <Button
                onClick={handleBack}
                startIcon={<ArrowBackIcon />}
                disabled={loading}>
                Back
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleNext}
              disabled={!isStepValid() || loading}
              endIcon={isLastStep ? <CheckCircleIcon /> : <ArrowForwardIcon />}>
              {isLastStep ? 'Finish & Save' : 'Next'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

CompanyOnboardingStepper.propTypes = {
  onSave: PropTypes.func.isRequired,
  initialData: PropTypes.object,
  loading: PropTypes.bool,
  logoUrl: PropTypes.string,
  onLogoUpload: PropTypes.func.isRequired,
  userName: PropTypes.string
};

export default CompanyOnboardingStepper;
